import {
  Course,
  CourseAuthor,
  CourseEnrollment,
  CourseType,
  EnrollmentStatus,
} from '../../../apis/api-client';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Typography,
} from '../../ui';
import {
  ChevronRightIcon,
  DateRangeIcon,
  PlayCircleIcon,
  SchoolIcon,
} from '../../icons';
import { courseTypeMeta } from '../contentTypeMeta';
import { formatDate } from '../../../utils/roleHelpers';

export interface EnrollmentActivityRowProps {
  enrollment: CourseEnrollment;
  /** Resolved content type (enrollment field, falling back to the
   *  cached course) so paths/events label correctly. */
  resolvedType: CourseType | undefined;
  isExpanded: boolean;
  /** Cached course object — undefined until the bulk fetch lands. */
  course?: Course;
  /** SAS-signed thumbnail URL, if resolved. */
  thumb?: string;
  /** Instructor resolved off the shared author directory. */
  author?: CourseAuthor;
  /** Task counts (lessons/sub-courses done vs total), inline from the enrollment. */
  tasks?: { total: number; done: number };
  onToggle: () => void;
  onOpenCourse: () => void;
}

/**
 * Two-digit MM-DD-YY format used by the "Assignment due …" pill.
 * Stays compact so it fits inside the amber chip without forcing the
 * row taller. Falls back to an em-dash when the input is missing.
 */
function formatShortDate(value: Date | string | undefined | null): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}-${dd}-${yy}`;
}

/**
 * EnrollmentActivityRow
 *
 * One expandable row in the learner "Your activity" panel. Header is
 * always rendered; the details block slides in when expanded and shows
 * thumbnail, description, instructor, task/overall progress, and a
 * Continue CTA. Purely presentational — all data (course/thumb/task
 * caches) is resolved by the parent and passed in. Extracted from
 * LearnerActivitySummary.
 */
export default function EnrollmentActivityRow({
  enrollment: e,
  resolvedType,
  isExpanded,
  course,
  thumb,
  author,
  tasks,
  onToggle,
  onOpenCourse,
}: EnrollmentActivityRowProps) {
  const meta = courseTypeMeta(resolvedType);
  const isPath = resolvedType === CourseType.LearningPath;
  const isExternal = resolvedType === CourseType.External;
  const isCompleted = e.enrollmentStatus === EnrollmentStatus.Completed;
  const detailsLoaded = course !== undefined;
  // Progress percentage — prefer whichever is higher between the
  // server's stored `progressPercentage` and the locally-computed
  // task ratio. The server number can lag behind a fresh lesson
  // completion (enrollment record doesn't refresh until the next
  // fetch), so when the task cache is populated we let it pull the
  // meter ahead. Once the server catches up — or exceeds local via
  // weighted lesson scoring — the server number wins.
  const taskPct =
    tasks && tasks.total > 0 ? Math.round((tasks.done / tasks.total) * 100) : 0;
  const pct = isCompleted
    ? 100
    : Math.max(
        0,
        Math.min(100, Math.max(Math.round(e.progressPercentage ?? 0), taskPct)),
      );
  // Label varies by content type so the count reads naturally.
  const taskLabel = isPath ? 'courses' : isExternal ? '' : 'modules';

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isExpanded ? 'primary.main' : 'divider',
        overflow: 'hidden',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: isExpanded ? 'primary.main' : 'primary.main',
          boxShadow: isExpanded ? undefined : '0 2px 6px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Row header — clickable to toggle. Single-row layout keeps
          each entry tight: icon · title (with enrollment date inline)
          · type/assigned chips · compact progress · View details. */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          py: 0.75,
          px: 1.25,
          cursor: 'pointer',
          bgcolor: isExpanded ? 'rgba(var(--brand-rgb), 0.08)' : 'transparent',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: `${meta.color}18`,
            color: meta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 16 },
          }}
        >
          {meta.icon}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {e.courseTitle || `Course #${e.courseId}`}
          </Typography>
          {(e.enrollmentCreatedAt || e.startedAt) && (
            <Typography
              variant="caption"
              color="text.disabled"
              noWrap
              sx={{ fontSize: '0.7rem', flexShrink: 0 }}
            >
              · Enrolled {formatDate(e.enrollmentCreatedAt || e.startedAt)}
            </Typography>
          )}
          <Chip
            label={meta.label}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {e.isAssigned && (
            <Chip
              label={
                e.deadline
                  ? `Assignment due ${formatShortDate(e.deadline)}`
                  : 'Assigned'
              }
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'warning.softBg',
                color: 'warning.softText',
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>
        {/* Compact inline progress — narrow bar + percent. Frees the
            second row that the old stacked layout consumed. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              width: 80,
              height: 4,
              borderRadius: 999,
              bgcolor: 'grey.100',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                // Completed rows keep the solid success color so a
                // finished course reads unambiguously; in-progress
                // rows pick up the brand cyan gradient.
                ...(isCompleted
                  ? { bgcolor: 'success.main' }
                  : {
                      background: 'var(--brand-gradient)',
                    }),
              },
            }}
          />
          <Typography
            variant="caption"
            fontWeight={600}
            color={isCompleted ? 'success.main' : 'primary.main'}
            sx={{ minWidth: 32, textAlign: 'right' }}
          >
            {pct}%
          </Typography>
        </Box>
        {/* Explicit "View details / Hide details" button — much more
            discoverable than a lone chevron. The whole row stays
            clickable, but the labeled button signals what tap does. */}
        <Button
          size="small"
          variant="text"
          onClick={(ev) => {
            ev.stopPropagation();
            onToggle();
          }}
          endIcon={
            <ChevronRightIcon
              sx={{
                transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
                transition: 'transform 150ms ease',
              }}
            />
          }
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.25,
            '& .MuiButton-endIcon': { ml: 0.25 },
          }}
        >
          {isExpanded ? 'Hide' : 'View details'}
        </Button>
      </Box>

      {/* Expanded details — thumbnail + description + instructor +
          tasks + progress + Continue CTA. The full Course (for the
          description/instructor/thumbnail) is lazy-fetched on first
          expand; task counts already ride inline on the row. Thumbnail
          stretches to the column's height and uses `contain` so the
          full image is visible — no crop. */}
      {isExpanded && (
        <Box sx={{ p: 2, pt: 1.5, bgcolor: 'background.paper' }}>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}
          >
            {/* Thumbnail — wider 16:9 band so the artwork carries
                more visual weight in the expanded row. Softly
                rounded corners (2 → 16px) tie it to the rest of the
                card system. */}
            <Box
              sx={{
                width: { xs: '100%', sm: 360 },
                aspectRatio: '16 / 9',
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
                display: 'flex',
                bgcolor: 'grey.50',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
              }}
            >
              {thumb ? (
                <Box
                  component="img"
                  src={thumb}
                  alt={e.courseTitle || ''}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : detailsLoaded ? (
                <Box sx={{ fontSize: 48, opacity: 0.4 }}>{meta.icon}</Box>
              ) : (
                <CircularProgress size={20} />
              )}
            </Box>

            {/* Meta column */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {course?.description ||
                  (detailsLoaded ? 'No description provided.' : 'Loading description…')}
              </Typography>

              {/* Instructor */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SchoolIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>Instructor:</strong>{' '}
                  {author?.name || (detailsLoaded ? 'Not assigned' : 'Loading…')}
                </Typography>
              </Box>

              {/* Assignment date + due date — only render on rows
                  that came in via an assignment. We surface both
                  dates on a single line so they read as a paired
                  fact, and flag the deadline in red once it's
                  passed (and we're not yet completed) so overdue
                  courses are unmistakable. */}
              {e.isAssigned && (e.assignmentCreatedAt || e.deadline) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <DateRangeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {e.assignmentCreatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      <strong>Assigned:</strong> {formatDate(e.assignmentCreatedAt)}
                    </Typography>
                  )}
                  {e.deadline &&
                    (() => {
                      const deadlineDate = new Date(e.deadline as any);
                      const isOverdue =
                        !isCompleted && deadlineDate.getTime() < Date.now();
                      return (
                        <Typography
                          variant="caption"
                          color={isOverdue ? 'error.main' : 'text.secondary'}
                          fontWeight={isOverdue ? 700 : undefined}
                        >
                          <strong>Due:</strong> {formatDate(e.deadline)}
                          {isOverdue ? ' (overdue)' : ''}
                        </Typography>
                      );
                    })()}
                </Box>
              )}

              {/* Task progress — fine-grained, only for non-event
                  content. Shows "3 of 5 modules" or "2 of 4 courses"
                  so learners can quickly see what's left without
                  diving into the course player. */}
              {!isExternal && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PlayCircleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    <strong>Tasks:</strong>{' '}
                    {tasks
                      ? tasks.total > 0
                        ? `${tasks.done} of ${tasks.total} ${taskLabel} completed`
                        : `No ${taskLabel} yet`
                      : 'Loading…'}
                  </Typography>
                </Box>
              )}

              {/* Overall progress meter */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Course progress
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={isCompleted ? 'success.main' : 'primary.main'}
                  >
                    {pct}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'grey.100',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      ...(isCompleted
                        ? { bgcolor: 'success.main' }
                        : {
                            background: 'var(--brand-gradient)',
                          }),
                    },
                  }}
                />
              </Box>

              {/* Continue CTA — bottom-right of the meta column.
                  Every row here is already enrolled, so the label is
                  always "Continue" (or "Review" when completed) —
                  no "Start" branch. Gradient cyan with white text
                  matches the brand primary CTA used elsewhere. */}
              <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<PlayCircleIcon />}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onOpenCourse();
                  }}
                  sx={{
                    color: '#fff',
                    '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                    px: 3,
                    textTransform: 'none',
                  }}
                >
                  {isCompleted ? 'Review' : 'Continue'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
